import type { ChecksTeeData } from "@stryker/core/types/mechanicalJobs";
import type { PolarYesNoUndefined } from "@stryker/core/types/section";
import { useEffect, useState } from "react";
import ExpandableSection from "@/components/job/ExpandableSection";
import PolarQuestion from "@/components/job/PolarQuestion";
import { Button } from "@/components/ui/button";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

// Define state interfaces
interface ChecksState {
  is_the_service_hf_acid: PolarYesNoUndefined;
  is_the_service_wet_h2s: PolarYesNoUndefined;
  is_this_a_flare_line: PolarYesNoUndefined;
  does_the_line_vibrate_or_hammer: PolarYesNoUndefined;
  do_you_want_to_add_external_pressure_calculation: PolarYesNoUndefined;
  do_you_want_to_have_small_cross_section: PolarYesNoUndefined;
  do_you_want_to_add_area_of_reinforcement_calculation?: PolarYesNoUndefined;
  do_you_need_minimum_clearance: PolarYesNoUndefined;
  do_you_wish_to_change_default_joint_efficiency: PolarYesNoUndefined;
  do_you_want_to_add_mdmt_to_package: PolarYesNoUndefined;
  mdmt: string;
  is_this_a_redesign: PolarYesNoUndefined;
  is_this_revision: PolarYesNoUndefined;
  revision: string;
  do_you_wish_to_change_default_checked_by: PolarYesNoUndefined;
}

// Initial state
const checksInitial: ChecksState = {
  is_the_service_hf_acid: undefined,
  is_the_service_wet_h2s: undefined,
  is_this_a_flare_line: undefined,
  does_the_line_vibrate_or_hammer: undefined,
  do_you_want_to_add_external_pressure_calculation: undefined,
  do_you_want_to_have_small_cross_section: undefined,
  do_you_want_to_add_area_of_reinforcement_calculation: undefined,
  do_you_need_minimum_clearance: undefined,
  do_you_wish_to_change_default_joint_efficiency: undefined,
  do_you_want_to_add_mdmt_to_package: undefined,
  mdmt: "",
  is_this_a_redesign: undefined,
  is_this_revision: undefined,
  revision: "",
  do_you_wish_to_change_default_checked_by: undefined,
};

const cardName = "checks";

const toSafeString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

export function Checks() {
  const { sections, toggleExpandableSection, setChecks } = useCUMechanicalJobStore();
  const savedChecks = sections.checks;
  const repairType = sections.repairType.data.repairType;

  const [checksQuestions, setChecksQuestions] = useState<ChecksState>(checksInitial);

  // Load saved data
  useEffect(() => {
    if (savedChecks.isDataFilled) {
      setChecksQuestions({
        is_the_service_hf_acid:
          savedChecks.data.is_the_service_hf_acid !== undefined
            ? savedChecks.data.is_the_service_hf_acid
              ? "yes"
              : "no"
            : undefined,
        is_the_service_wet_h2s:
          savedChecks.data.is_the_service_wet_h2s !== undefined
            ? savedChecks.data.is_the_service_wet_h2s
              ? "yes"
              : "no"
            : undefined,
        is_this_a_flare_line:
          savedChecks.data.is_this_a_flare_line !== undefined
            ? savedChecks.data.is_this_a_flare_line
              ? "yes"
              : "no"
            : undefined,
        does_the_line_vibrate_or_hammer:
          savedChecks.data.does_the_line_vibrate_or_hammer !== undefined
            ? savedChecks.data.does_the_line_vibrate_or_hammer
              ? "yes"
              : "no"
            : undefined,
        do_you_want_to_add_external_pressure_calculation:
          savedChecks.data.do_you_want_to_add_external_pressure_calculation !== undefined
            ? savedChecks.data.do_you_want_to_add_external_pressure_calculation
              ? "yes"
              : "no"
            : undefined,
        do_you_want_to_have_small_cross_section:
          savedChecks.data.do_you_want_to_have_small_cross_section !== undefined
            ? savedChecks.data.do_you_want_to_have_small_cross_section
              ? "yes"
              : "no"
            : undefined,
        ...(repairType === "tee" && {
          do_you_want_to_add_area_of_reinforcement_calculation:
            (savedChecks.data as ChecksTeeData).do_you_want_to_add_area_of_reinforcement_calculation !== undefined
              ? (savedChecks.data as ChecksTeeData).do_you_want_to_add_area_of_reinforcement_calculation
                ? "yes"
                : "no"
              : undefined,
        }),
        do_you_need_minimum_clearance:
          savedChecks.data.do_you_need_minimum_clearance !== undefined
            ? savedChecks.data.do_you_need_minimum_clearance
              ? "yes"
              : "no"
            : undefined,
        do_you_wish_to_change_default_joint_efficiency:
          savedChecks.data.do_you_wish_to_change_default_joint_efficiency !== undefined
            ? savedChecks.data.do_you_wish_to_change_default_joint_efficiency
              ? "yes"
              : "no"
            : undefined,
        do_you_want_to_add_mdmt_to_package:
          savedChecks.data.do_you_want_to_add_mdmt_to_package !== undefined
            ? savedChecks.data.do_you_want_to_add_mdmt_to_package
              ? "yes"
              : "no"
            : undefined,
        mdmt: toSafeString(savedChecks.data.mdmt),
        is_this_a_redesign:
          savedChecks.data.is_this_a_redesign !== undefined
            ? savedChecks.data.is_this_a_redesign
              ? "yes"
              : "no"
            : undefined,
        is_this_revision:
          savedChecks.data.is_this_revision !== undefined
            ? savedChecks.data.is_this_revision
              ? "yes"
              : "no"
            : undefined,
        revision: toSafeString(savedChecks.data.revision),
        do_you_wish_to_change_default_checked_by:
          savedChecks.data.do_you_wish_to_change_default_checked_by !== undefined
            ? savedChecks.data.do_you_wish_to_change_default_checked_by
              ? "yes"
              : "no"
            : undefined,
      });
    }
  }, [savedChecks, repairType]);

  // Handler functions following CompliantRepair pattern
  // NOTE: handleHfAcid / handleWetH2s / handleFlareLine / handleVibrateHammer / handleExternalPressure /
  // handleAreaReinforcement are currently unused because their questions are hidden (see JSX below).
  // Kept intentionally so the questions can be restored without rewriting the logic.
  // biome-ignore lint/correctness/noUnusedVariables: kept for hidden questions, see note above
  const handleHfAcid = (value: PolarYesNoUndefined) => {
    setChecksQuestions({
      is_the_service_hf_acid: value,
      is_the_service_wet_h2s: undefined,
      is_this_a_flare_line: undefined,
      does_the_line_vibrate_or_hammer: undefined,
      do_you_want_to_add_external_pressure_calculation: undefined,
      do_you_want_to_have_small_cross_section: undefined,
      do_you_want_to_add_area_of_reinforcement_calculation: undefined,
      do_you_need_minimum_clearance: undefined,
      do_you_wish_to_change_default_joint_efficiency: undefined,
      do_you_want_to_add_mdmt_to_package: undefined,
      mdmt: "",
      is_this_a_redesign: undefined,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    });
  };

  // biome-ignore lint/correctness/noUnusedVariables: kept for hidden question, see note above
  const handleWetH2s = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      is_the_service_wet_h2s: value,
      is_this_a_flare_line: undefined,
      does_the_line_vibrate_or_hammer: undefined,
      do_you_want_to_add_external_pressure_calculation: undefined,
      do_you_want_to_have_small_cross_section: undefined,
      do_you_want_to_add_area_of_reinforcement_calculation: undefined,
      do_you_need_minimum_clearance: undefined,
      do_you_wish_to_change_default_joint_efficiency: undefined,
      do_you_want_to_add_mdmt_to_package: undefined,
      mdmt: "",
      is_this_a_redesign: undefined,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  // biome-ignore lint/correctness/noUnusedVariables: kept for hidden question, see note above
  const handleFlareLine = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      is_this_a_flare_line: value,
      does_the_line_vibrate_or_hammer: undefined,
      do_you_want_to_add_external_pressure_calculation: undefined,
      do_you_want_to_have_small_cross_section: undefined,
      do_you_want_to_add_area_of_reinforcement_calculation: undefined,
      do_you_need_minimum_clearance: undefined,
      do_you_wish_to_change_default_joint_efficiency: undefined,
      do_you_want_to_add_mdmt_to_package: undefined,
      mdmt: "",
      is_this_a_redesign: undefined,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  // biome-ignore lint/correctness/noUnusedVariables: kept for hidden question, see note above
  const handleVibrateHammer = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      does_the_line_vibrate_or_hammer: value,
      do_you_want_to_add_external_pressure_calculation: undefined,
      do_you_want_to_have_small_cross_section: undefined,
      do_you_want_to_add_area_of_reinforcement_calculation: undefined,
      do_you_need_minimum_clearance: undefined,
      do_you_wish_to_change_default_joint_efficiency: undefined,
      do_you_want_to_add_mdmt_to_package: undefined,
      mdmt: "",
      is_this_a_redesign: undefined,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  // biome-ignore lint/correctness/noUnusedVariables: kept for hidden question, see note above
  const handleExternalPressure = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      do_you_want_to_add_external_pressure_calculation: value,
      do_you_want_to_have_small_cross_section: undefined,
      do_you_want_to_add_area_of_reinforcement_calculation: undefined,
      do_you_need_minimum_clearance: undefined,
      do_you_wish_to_change_default_joint_efficiency: undefined,
      do_you_want_to_add_mdmt_to_package: undefined,
      mdmt: "",
      is_this_a_redesign: undefined,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  const handleSmallCrossSection = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      do_you_want_to_have_small_cross_section: value,
      do_you_want_to_add_area_of_reinforcement_calculation: undefined,
      do_you_need_minimum_clearance: undefined,
      do_you_wish_to_change_default_joint_efficiency: undefined,
      do_you_want_to_add_mdmt_to_package: undefined,
      mdmt: "",
      is_this_a_redesign: undefined,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  // biome-ignore lint/correctness/noUnusedVariables: kept for hidden question, see note above
  const handleAreaReinforcement = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      do_you_want_to_add_area_of_reinforcement_calculation: value,
      do_you_need_minimum_clearance: undefined,
      do_you_wish_to_change_default_joint_efficiency: undefined,
      do_you_want_to_add_mdmt_to_package: undefined,
      mdmt: "",
      is_this_a_redesign: undefined,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  const handleMinimumClearance = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      do_you_need_minimum_clearance: value,
      do_you_wish_to_change_default_joint_efficiency: undefined,
      do_you_want_to_add_mdmt_to_package: undefined,
      mdmt: "",
      is_this_a_redesign: undefined,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  const handleJointEfficiency = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      do_you_wish_to_change_default_joint_efficiency: value,
      do_you_want_to_add_mdmt_to_package: undefined,
      mdmt: "",
      is_this_a_redesign: undefined,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  const handleAddMdmtToPackage = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      do_you_want_to_add_mdmt_to_package: value,
      mdmt: value === "yes" ? prev.mdmt : "",
      is_this_a_redesign: undefined,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  const handleRedesign = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      is_this_a_redesign: value,
      is_this_revision: undefined,
      revision: "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  const handleRevisionQuestion = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      is_this_revision: value,
      revision: value === "yes" ? prev.revision : "",
      do_you_wish_to_change_default_checked_by: undefined,
    }));
  };

  const handleCheckedBy = (value: PolarYesNoUndefined) => {
    setChecksQuestions((prev) => ({
      ...prev,
      do_you_wish_to_change_default_checked_by: value,
    }));
  };

  // Minimum clearance question only applies to straight_line, tee, and long_radius (90) repair types.
  // It is also hidden for the 90 (long_radius) flow for now.
  const supportsMinimumClearance = repairType !== "flange_clamp";
  const showMinimumClearance = supportsMinimumClearance && repairType !== "long_radius";
  // "Small cross section" is hidden for the 90 (long_radius) flow for now.
  const showSmallCrossSection = repairType !== "long_radius";

  // Check if all questions are answered
  const areAllQuestionsAnswered = () => {
    const baseQuestionsAnswered =
      // Hidden for all flows for now, defaults sent as "no":
      // checksQuestions.is_the_service_hf_acid !== undefined &&
      // checksQuestions.is_the_service_wet_h2s !== undefined &&
      // checksQuestions.is_this_a_flare_line !== undefined &&
      // checksQuestions.does_the_line_vibrate_or_hammer !== undefined &&
      // checksQuestions.do_you_want_to_add_external_pressure_calculation !== undefined &&
      (!showSmallCrossSection || checksQuestions.do_you_want_to_have_small_cross_section !== undefined) &&
      checksQuestions.do_you_wish_to_change_default_joint_efficiency !== undefined &&
      checksQuestions.do_you_want_to_add_mdmt_to_package !== undefined &&
      checksQuestions.is_this_a_redesign !== undefined &&
      checksQuestions.is_this_revision !== undefined &&
      checksQuestions.do_you_wish_to_change_default_checked_by !== undefined;

    const minimumClearanceValid = !showMinimumClearance || checksQuestions.do_you_need_minimum_clearance !== undefined;

    return baseQuestionsAnswered && minimumClearanceValid;
  };

  const handleSaveChecks = () => {
    // Convert "yes"/"no" string values to boolean values
    const convertedData = {
      is_the_service_hf_acid: checksQuestions.is_the_service_hf_acid === "yes",
      is_the_service_wet_h2s: checksQuestions.is_the_service_wet_h2s === "yes",
      is_this_a_flare_line: checksQuestions.is_this_a_flare_line === "yes",
      does_the_line_vibrate_or_hammer: checksQuestions.does_the_line_vibrate_or_hammer === "yes",
      do_you_want_to_add_external_pressure_calculation:
        checksQuestions.do_you_want_to_add_external_pressure_calculation === "yes",
      do_you_want_to_have_small_cross_section: checksQuestions.do_you_want_to_have_small_cross_section === "yes",
      ...(showMinimumClearance &&
        checksQuestions.do_you_need_minimum_clearance !== undefined && {
          do_you_need_minimum_clearance: checksQuestions.do_you_need_minimum_clearance === "yes",
        }),
      do_you_wish_to_change_default_joint_efficiency:
        checksQuestions.do_you_wish_to_change_default_joint_efficiency === "yes",
      do_you_want_to_add_mdmt_to_package: checksQuestions.do_you_want_to_add_mdmt_to_package === "yes",
      mdmt:
        checksQuestions.do_you_want_to_add_mdmt_to_package === "yes" && checksQuestions.mdmt.trim().length > 0
          ? Number.parseFloat(checksQuestions.mdmt)
          : null,
      is_this_a_redesign: checksQuestions.is_this_a_redesign === "yes",
      is_this_revision: checksQuestions.is_this_revision === "yes",
      revision:
        checksQuestions.is_this_revision === "yes" && checksQuestions.revision.trim().length > 0
          ? Number.parseFloat(checksQuestions.revision)
          : null,
      do_you_wish_to_change_default_checked_by: checksQuestions.do_you_wish_to_change_default_checked_by === "yes",
      // "Area of reinforcement calculation" question is hidden for tees for now; send it as false.
      ...(repairType === "tee" && {
        do_you_want_to_add_area_of_reinforcement_calculation: false,
      }),
    };

    setChecks({
      data: convertedData,
      status: "done",
    });
  };

  return (
    <ExpandableSection
      setIsExpanded={() => toggleExpandableSection(cardName)}
      status={savedChecks.status}
      title="Checks"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          {/* Hidden for all flows for now. Values are still submitted, defaulting to "no". */}
          {/*
          <PolarQuestion
            shouldRender={true}
            question="Is the service HF acid?"
            onNoClick={(e) => {
              e.stopPropagation();
              handleHfAcid("no");
            }}
            onYesClick={(e) => {
              e.stopPropagation();
              handleHfAcid("yes");
            }}
            selection={checksQuestions.is_the_service_hf_acid}
          />

          <PolarQuestion
            shouldRender={checksQuestions.is_the_service_hf_acid !== undefined}
            question="Is the service wet H2S?"
            onNoClick={(e) => {
              e.stopPropagation();
              handleWetH2s("no");
            }}
            onYesClick={(e) => {
              e.stopPropagation();
              handleWetH2s("yes");
            }}
            selection={checksQuestions.is_the_service_wet_h2s}
          />

          <PolarQuestion
            shouldRender={checksQuestions.is_the_service_wet_h2s !== undefined}
            question="Is this a flare line?"
            onNoClick={(e) => {
              e.stopPropagation();
              handleFlareLine("no");
            }}
            onYesClick={(e) => {
              e.stopPropagation();
              handleFlareLine("yes");
            }}
            selection={checksQuestions.is_this_a_flare_line}
          />

          <PolarQuestion
            shouldRender={checksQuestions.is_this_a_flare_line !== undefined}
            question="Does the line vibrate or hammer?"
            onNoClick={(e) => {
              e.stopPropagation();
              handleVibrateHammer("no");
            }}
            onYesClick={(e) => {
              e.stopPropagation();
              handleVibrateHammer("yes");
            }}
            selection={checksQuestions.does_the_line_vibrate_or_hammer}
          />

          <PolarQuestion
            shouldRender={checksQuestions.does_the_line_vibrate_or_hammer !== undefined}
            question="Do you want to add external pressure calculation?"
            onNoClick={(e) => {
              e.stopPropagation();
              handleExternalPressure("no");
            }}
            onYesClick={(e) => {
              e.stopPropagation();
              handleExternalPressure("yes");
            }}
            selection={checksQuestions.do_you_want_to_add_external_pressure_calculation}
          />
          */}

          {/* Hidden for the 90 (long_radius) flow for now. */}
          {showSmallCrossSection && (
            <PolarQuestion
              shouldRender={true}
              question="Do you want to have small cross section?"
              onNoClick={(e) => {
                e.stopPropagation();
                handleSmallCrossSection("no");
              }}
              onYesClick={(e) => {
                e.stopPropagation();
                handleSmallCrossSection("yes");
              }}
              selection={checksQuestions.do_you_want_to_have_small_cross_section}
            />
          )}

          {/* Hidden for tees for now (this question only ever rendered for tees). */}
          {/*
          {repairType === "tee" && (
            <PolarQuestion
              shouldRender={checksQuestions.do_you_want_to_have_small_cross_section !== undefined}
              question="Do you want to add area of reinforcement calculation?"
              onNoClick={(e) => {
                e.stopPropagation();
                handleAreaReinforcement("no");
              }}
              onYesClick={(e) => {
                e.stopPropagation();
                handleAreaReinforcement("yes");
              }}
              selection={checksQuestions.do_you_want_to_add_area_of_reinforcement_calculation}
            />
          )}
          */}

          {showMinimumClearance && (
            <PolarQuestion
              shouldRender={
                showSmallCrossSection ? checksQuestions.do_you_want_to_have_small_cross_section !== undefined : true
              }
              question="Do you need a minimum clearance?"
              onNoClick={(e) => {
                e.stopPropagation();
                handleMinimumClearance("no");
              }}
              onYesClick={(e) => {
                e.stopPropagation();
                handleMinimumClearance("yes");
              }}
              selection={checksQuestions.do_you_need_minimum_clearance}
            />
          )}

          <PolarQuestion
            shouldRender={
              showMinimumClearance
                ? checksQuestions.do_you_need_minimum_clearance !== undefined
                : showSmallCrossSection
                  ? checksQuestions.do_you_want_to_have_small_cross_section !== undefined
                  : true
            }
            question="Do you wish to change the default Joint Efficiency, Corrosion Allowance, or Mill Tolerance?"
            onNoClick={(e) => {
              e.stopPropagation();
              handleJointEfficiency("no");
            }}
            onYesClick={(e) => {
              e.stopPropagation();
              handleJointEfficiency("yes");
            }}
            selection={checksQuestions.do_you_wish_to_change_default_joint_efficiency}
          />

          <PolarQuestion
            shouldRender={checksQuestions.do_you_wish_to_change_default_joint_efficiency !== undefined}
            question="Do you want to add MDMT to package?"
            onNoClick={(e) => {
              e.stopPropagation();
              handleAddMdmtToPackage("no");
            }}
            onYesClick={(e) => {
              e.stopPropagation();
              handleAddMdmtToPackage("yes");
            }}
            selection={checksQuestions.do_you_want_to_add_mdmt_to_package}
          />

          <PolarQuestion
            shouldRender={checksQuestions.do_you_want_to_add_mdmt_to_package !== undefined}
            question="Is this a redesign?"
            onNoClick={(e) => {
              e.stopPropagation();
              handleRedesign("no");
            }}
            onYesClick={(e) => {
              e.stopPropagation();
              handleRedesign("yes");
            }}
            selection={checksQuestions.is_this_a_redesign}
          />

          <PolarQuestion
            shouldRender={checksQuestions.is_this_a_redesign !== undefined}
            question="Is this revision?"
            onNoClick={(e) => {
              e.stopPropagation();
              handleRevisionQuestion("no");
            }}
            onYesClick={(e) => {
              e.stopPropagation();
              handleRevisionQuestion("yes");
            }}
            selection={checksQuestions.is_this_revision}
          />

          <PolarQuestion
            shouldRender={checksQuestions.is_this_revision !== undefined}
            question="Do you want to change the default Checked By value?"
            onNoClick={(e) => {
              e.stopPropagation();
              handleCheckedBy("no");
            }}
            onYesClick={(e) => {
              e.stopPropagation();
              handleCheckedBy("yes");
            }}
            selection={checksQuestions.do_you_wish_to_change_default_checked_by}
          />
        </div>

        <Button onClick={handleSaveChecks} className="self-end" disabled={!areAllQuestionsAnswered()}>
          Save
        </Button>
      </div>
    </ExpandableSection>
  );
}
